/**
 * Business Rules API client.
 *
 * All previously-hardcoded business logic now comes from the backend:
 *   - Tax rate (was 8% hardcoded in 3 files)
 *   - Financing options (was hardcoded interest rates)
 *   - Trade-in valuation (was 3 different formulas)
 *   - Service pricing (was a 10+-function engine in pages/services/utils)
 *   - Energy savings estimates

 *
 * Use these helpers everywhere you need pricing, eligibility, or thresholds.
 */

import { apiFetch } from './api';
import { API_ENDPOINTS } from '../config/api-endpoints';

export interface FinancingOption {
  months: number;
  interestRate: number;
  processingFee: number;
  label: string;
  monthlyAmount: number;
  totalAmount: number;
}

export interface BusinessRules {
  taxRate: number;
  financingOptions: Array<Omit<FinancingOption, 'monthlyAmount' | 'totalAmount'>>;
  tradeInMultipliers: { excellent: number; good: number; fair: number; poor: number };
  tradeInDepreciation: number;
  warrantyExpiringDays: number;
  lowHealthThreshold: number;
  energyKwhRate: number;
  energyEfficiencyGain: number;
  servicePricing: Record<string, unknown>;
}

export interface TradeInQuote {
  value: number;
  conditionMultiplier: number;
  depreciationFactor: number;
}

export interface ServicePriceQuote {
  basePrice: number;
  unitsCost: number;
  multiplier: number;
  addOnsCost: number;
  distanceFee: number;
  accessMultiplier: number;
  frequencyDiscount: number;
  total: number;
}

export interface EnergySavingsQuote {
  annualSavings: number;
  kwhRate: number;
  efficiencyGain: number;
  currentAnnualCost?: number;
}

let cachedRules: BusinessRules | null = null;
let cacheLoadedAt = 0;
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Fetch all business rules. Result is cached for 5 minutes since these
 * change very rarely. Use clearBusinessRulesCache() if you have just
 * updated tenant settings.
 */
export async function getBusinessRules(): Promise<BusinessRules> {
  const now = Date.now();
  if (cachedRules && now - cacheLoadedAt < CACHE_TTL_MS) return cachedRules;
  const res = await apiFetch(API_ENDPOINTS.BUSINESS_RULES.GET) as any;
  cachedRules = res.data as BusinessRules;
  cacheLoadedAt = now;
  return cachedRules;
}

export function clearBusinessRulesCache(): void {
  cachedRules = null;
  cacheLoadedAt = 0;
}

export async function getFinancingPlans(principal: number): Promise<FinancingOption[]> {
  const res = await apiFetch(API_ENDPOINTS.BUSINESS_RULES.FINANCING, {
    method: 'POST',
    body: { principal },
  }) as any;
  return (res.data as FinancingOption[]) ?? [];
}

export async function quoteTradeIn(args: {
  baseValue: number;
  condition: 'excellent' | 'good' | 'fair' | 'poor';
  ageYears: number;
}): Promise<TradeInQuote> {
  const res = await apiFetch(API_ENDPOINTS.BUSINESS_RULES.TRADE_IN, {
    method: 'POST',
    body: args,
  }) as any;
  return res.data as TradeInQuote;
}

export async function quoteServicePrice(args: {
  serviceType: string;
  quantity?: number;
  sizeOption?: string;
  addOns?: string[];
  distanceMiles?: number;
  accessOption?: string;
  frequencyDiscountPct?: number;
}): Promise<ServicePriceQuote> {
  const res = await apiFetch(API_ENDPOINTS.BUSINESS_RULES.SERVICE_PRICE, {
    method: 'POST',
    body: args,
  }) as any;
  return res.data as ServicePriceQuote;
}

export async function quoteEnergySavings(args: {
  currentKwhPerYear: number;
  replacementKwhPerYear?: number;
}): Promise<EnergySavingsQuote> {
  const res = await apiFetch(API_ENDPOINTS.BUSINESS_RULES.ENERGY_SAVINGS, {
    method: 'POST',
    body: args,
  }) as any;
  return res.data as EnergySavingsQuote;
}
